import { type Ref, computed, unref, useStore } from "@nuxtjs/composition-api";

import { useI18n, useLinkService } from "@/composables";
import { getProductDetailsStore } from "@/store/product-details";
import { getProductsStore } from "@/store/products";
/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProduct
 * @param    {Ref<string> | string} productId
 * @desc     A composable that returns a product and it's product details
 *
 * @summary  Composable used to retrieve a single product and it's product details.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProduct = (productId: Ref<string> | string) => {
  const store = useStore();
  const productsStore = getProductsStore(store);
  const productDetailsStore = getProductDetailsStore(store);

  const product = computed(() => productsStore.products[unref(productId)]);
  const productDetails = computed(() => productDetailsStore.productDetails[product.value?.productDetailsId ?? ""]);
  const name = computed(() => {
    return productDetails.value?.name ?? "product";
  });
  const productDetailsId = computed(() => product.value?.productDetailsId ?? "");
  const ltkId = computed(() => product.value?.ltkId ?? "");
  const advertiserName = computed(() => {
    return productDetails.value?.advertiserName ?? "retailer";
  });
  const isRetailerProduct = computed(() => {
    if (!productDetails.value) {
      return false;
    }
    const { catalogRetailerProductId, hasVariants } = productDetails.value;
    return Boolean(hasVariants && catalogRetailerProductId);
  });

  return { product, productDetails, productDetailsId, ltkId, name, advertiserName, isRetailerProduct };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProductPricing
 * @param    {Ref<string> | string} productId
 * @param    {Ref<number> | number} discountPercent
 * @desc     A composable that calculates the price and discount price for a product
 *           given the product details ID and discount percentage for the product.
 *
 * @summary  Composable used to calculate pricing for a product.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProductPricing = (productId: Ref<string> | string, discountPercent: Ref<number> | number) => {
  const percentageDelta = 100;
  const i18n = useI18n();

  const { productDetails } = useProduct(productId);

  const price = computed(() => {
    if (!productDetails.value) {
      return Intl.NumberFormat(i18n.locale, { style: "currency", currency: "USD" }).format(0);
    }
    const { price = 0, localPrice, currency } = productDetails.value;
    if (localPrice && currency) {
      return Intl.NumberFormat(i18n.locale, { style: "currency", currency }).format(localPrice);
    }
    return Intl.NumberFormat(i18n.locale, { style: "currency", currency: "USD" }).format(Number(price));
  });

  const discountPrice = computed(() => {
    if (!productDetails.value) {
      return Intl.NumberFormat(i18n.locale, { style: "currency", currency: "USD" }).format(0);
    }
    const calculateDiscountPrice = (originalPrice: number) => {
      return originalPrice - (originalPrice * unref(discountPercent)) / percentageDelta;
    };

    const { price = 0, localPrice, currency } = productDetails.value;

    if (localPrice && currency) {
      return Intl.NumberFormat(i18n.locale, { style: "currency", currency }).format(calculateDiscountPrice(localPrice));
    }
    return Intl.NumberFormat(i18n.locale, { style: "currency", currency: "USD" }).format(calculateDiscountPrice(price));
  });

  return { price, discountPrice };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProductImageProps
 * @param     {object} obj
 * @param    {Ref<string> | string} obj.productId
 * @param    {string} obj.width
 * @param    {string} obj.height
 * @desc     A composable that calculates the props for a product image component.
 *           Specifically, this composable calculates the image `src`, `alt`, `title`,
 *           and `crossorigin` props.
 *
 * @summary  Composable used to calculate props for a product image component.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProductImageProps = ({
  productId,
  width,
  height,
}: {
  productId: Ref<string> | string;
  width?: string;
  height?: string;
}) => {
  const linkService = useLinkService();
  const { product, name, advertiserName, productDetails } = useProduct(productId);

  const src = computed(() => {
    // No image src to render
    if (!product.value) {
      return "";
    }
    // If the product is a Shopify product, then do not add query parameters to the image URl.
    if (productDetails.value?.hasVariants) {
      return product.value.imageUrl;
    }
    return linkService.getImageLink({
      url: product.value.imageUrl,
      width: width || "256",
      height: height || "256",
      crop: true,
      useDefaultDpr: false,
    });
  });

  const imageProps = computed(() => {
    const alt = `${name.value} | ${advertiserName.value}`;
    return { src: src.value, alt, title: alt, crossorigin: false };
  });

  return imageProps;
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProductLinkProps
 * @param    {Ref<string> | string} productId
 * @desc     A composable that calculates the props for a product link.
 *           Specifically, this composable calculates the link `href`, `title`, `target`,
 *           and `rel` props.
 *
 * @summary  Composable used to calculate props for a product link.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProductLinkProps = (productId: Ref<string> | string) => {
  const linkService = useLinkService();
  const { product, productDetails, name, advertiserName } = useProduct(productId);

  const isRetailerProduct = computed(() => {
    if (!productDetails.value) {
      return false;
    }
    const { catalogRetailerProductId, hasVariants } = productDetails.value;
    return Boolean(hasVariants && catalogRetailerProductId);
  });

  const href = computed(() => {
    if (!product.value) {
      return "";
    }
    const { productDetailsId, ltkId, hyperlink, links } = product.value;
    // If the product is a retailer product, then create a relative link to the PDP.
    if (isRetailerProduct.value) {
      return linkService.getProductDetailsLink({
        productDetailsId,
        ltkId,
      });
    }
    // Else if the new hyperlink is available, then use the hyperlink to create a product link.
    if (hyperlink) {
      return linkService.getProductLink({
        url: hyperlink,
        ltkId,
        priceDropPercent: productDetails.value?.percentChange,
      });
    }
    // Else, fallback to old style product link.
    return links.ltkWeb ?? "";
  });

  const productLinkProps = computed(() => ({
    ...(isRetailerProduct.value
      ? {
          to: href.value,
          href: undefined,
        }
      : {
          href: href.value,
          target: "_blank",
          rel: "nofollow noopener",
        }),
    title: `link to ${name.value} from ${advertiserName.value}`,
  }));

  return productLinkProps;
};
